import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { DataTable, PageTitle } from '@/components';
import { Loader } from '@/components/ui';
import { useGetProfessional, useGetProfessionalMonthlyUsage } from '@/services';
import { formatProfessionalFullName } from '@/utils';
import { getBackendErrorMessage } from '@/utils/errorMessage';
import { ProfessionalMonthlyUsageColumns } from './components/ProfessionalMonthlyUsageColumns';
import { ProfessionalMonthlyUsageFilters } from './components/ProfessionalMonthlyUsageFilters';
import { ProfessionalUsageSwitcher } from './components/ProfessionalUsageSwitcher';

const isNotFoundError = (error: unknown) =>
	axios.isAxiosError(error) && error.response?.status === 404;

const columns = ProfessionalMonthlyUsageColumns();

const AdminProfessionalMonthlyUsage = () => {
	const navigate = useNavigate();
	const { id: professionalId } = useParams<{ id: string }>();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(12);
	const [usageYear, setUsageYear] = useState<number | undefined>();
	const [usageMonth, setUsageMonth] = useState<number | undefined>();

	const {
		data: professional,
		isLoading: isLoadingProfessional,
		isError: isProfessionalError,
		error: professionalError,
	} = useGetProfessional(professionalId ?? '', {
		enabled: !!professionalId,
	});

	const {
		data: usageData,
		isLoading: isLoadingUsage,
		isError: isUsageError,
		error: usageError,
	} = useGetProfessionalMonthlyUsage(
		professionalId ?? '',
		{
			page,
			limit,
			...(usageYear != null ? { usage_year: usageYear } : {}),
			...(usageMonth != null ? { usage_month: usageMonth } : {}),
		},
		{ enabled: !!professionalId },
	);

	useEffect(() => {
		setPage(1);
	}, [usageYear, usageMonth, limit]);

	useEffect(() => {
		setPage(1);
		setUsageYear(undefined);
		setUsageMonth(undefined);
	}, [professionalId]);

	const handleSelectProfessional = (id: string) => {
		navigate(`/admin/profissionais/editar/${id}/uso`, { replace: true });
	};

	if (!professionalId) {
		navigate('/admin/profissionais', { replace: true });
		return null;
	}

	const professionalName = professional
		? formatProfessionalFullName(professional)
		: 'Profissional';
	const professionalEmail = professional?.email?.trim() || '—';
	const usageItems = usageData?.data ?? [];
	const totalItems = usageData?.pagination?.total ?? 0;
	const isLoading = isLoadingProfessional || isLoadingUsage;
	const notFound =
		isNotFoundError(professionalError) || isNotFoundError(usageError);
	const requestError =
		!notFound && (isProfessionalError || isUsageError)
			? getBackendErrorMessage(professionalError ?? usageError)
			: null;

	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="flex items-start gap-4">
				<button
					type="button"
					onClick={() => navigate('/admin/profissionais')}
					className="group mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
					aria-label="Voltar"
				>
					<ArrowLeft
						size={20}
						className="text-stone-400 group-hover:text-stone-900"
					/>
				</button>
				<div className="min-w-0 space-y-1">
					<PageTitle
						text={
							<span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
								<span>Uso mensal —</span>
								<span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
									<span className="truncate">
										{professionalName}
									</span>
									{professional && (
										<ProfessionalUsageSwitcher
											currentProfessionalId={
												professionalId
											}
											onSelect={handleSelectProfessional}
										/>
									)}
								</span>
							</span>
						}
					/>
					<p className="text-sm text-stone-400">
						Consumo de Claude (IA) e Daily.co (vídeo) por mês civil
						(UTC).
					</p>
					{professional && (
						<p className="text-sm text-stone-500">
							{professionalEmail}
						</p>
					)}
				</div>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-24">
					<Loader />
				</div>
			) : notFound ? (
				<div className="rounded-2xl border border-stone-200 bg-white px-6 py-12 text-center">
					<p className="text-sm font-semibold text-stone-800">
						Profissional não encontrado
					</p>
					<p className="mt-2 text-sm text-stone-500">
						O profissional pode ter sido removido ou o link está
						inválido.
					</p>
				</div>
			) : (
				<>
					{requestError && (
						<p className="text-sm text-red-600" role="alert">
							{requestError}
						</p>
					)}

					<ProfessionalMonthlyUsageFilters
						usageYear={usageYear}
						usageMonth={usageMonth}
						onUsageYearChange={setUsageYear}
						onUsageMonthChange={setUsageMonth}
					/>

					<div className="space-y-1">
						<h3 className="text-lg font-black text-stone-900">
							Histórico de consumo
						</h3>
						<p className="text-sm text-stone-400">
							{totalItems} registro{totalItems !== 1 ? 's' : ''}{' '}
							mensal
							{totalItems !== 1 ? 'es' : ''}
						</p>
					</div>

					{usageItems.length === 0 ? (
						<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-white px-6 py-16 text-center">
							<div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100 text-stone-500">
								<BarChart3 className="size-7" />
							</div>
							<h4 className="text-base font-bold text-stone-900">
								Sem uso registrado
							</h4>
							<p className="mt-2 max-w-sm text-sm text-stone-500">
								Este profissional ainda não possui consumo
								mensal contabilizado
								{usageYear || usageMonth
									? ' para o período selecionado.'
									: '.'}
							</p>
						</div>
					) : (
						<DataTable
							tableClassName="table-fixed w-full"
							columns={columns}
							data={usageItems}
							page={page}
							limit={limit}
							totalItems={totalItems}
							onPageChange={handlePageChange}
							onLimitChange={handleLimitChange}
						/>
					)}
				</>
			)}
		</section>
	);
};

export { AdminProfessionalMonthlyUsage };
